import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Linkedin } from 'lucide-react'
import { apiAssetUrl } from '../../config'
import { adminApi } from '../api'
import ImageUpload from '../ImageUpload'
import { IMAGE_SIZE_HINTS } from '../media'
import { Btn, Card, Field, FormSkeleton, areaClass, inputClass } from '../ui'
import type { TeamDetail } from './teamShared'

type FormState = {
  name: string
  title: string
  bio: string
  linkedin_url: string
  is_active: boolean
  sort_order: string
  photo_path: string | null
  photo_stored_path: string | null
}

const empty = (): FormState => ({
  name: '',
  title: '',
  bio: '',
  linkedin_url: '',
  is_active: true,
  sort_order: '0',
  photo_path: null,
  photo_stored_path: null,
})

export default function TeamFormPage() {
  const { id } = useParams()
  const isNew = !id
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(empty())
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (isNew) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const { res, data } = await adminApi<{ member?: TeamDetail }>(`/api/admin/website/team/${id}`)
      if (cancelled) return
      const m = data.member
      if (!res.ok || !m) {
        setErr('Team member not found')
        setLoading(false)
        return
      }
      setForm({
        name: String(m.name || ''),
        title: String(m.title || ''),
        bio: String(m.bio || ''),
        linkedin_url: String(m.linkedin_url || ''),
        is_active: !!m.is_active,
        sort_order: String(m.sort_order ?? 0),
        photo_path: m.photo_path || null,
        photo_stored_path: m.photo_stored_path || null,
      })
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, isNew])

  async function save(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSaving(true)
    const fd = new FormData()
    fd.set('name', form.name)
    fd.set('title', form.title)
    fd.set('bio', form.bio)
    fd.set('linkedin_url', form.linkedin_url)
    fd.set('is_active', form.is_active ? '1' : '0')
    fd.set('sort_order', form.sort_order)
    fd.set('photo_path', form.photo_stored_path || form.photo_path || '')

    const path = isNew ? '/api/admin/website/team' : `/api/admin/website/team/${id}`
    const { res, data } = await adminApi<{ message?: string; id?: number }>(path, {
      method: isNew ? 'POST' : 'PUT',
      formData: fd,
    })
    setSaving(false)
    if (!res.ok) {
      setErr(data.message || 'Save failed')
      return
    }
    navigate('/admin/team', { replace: true })
  }

  if (loading) return <FormSkeleton />

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Btn variant="ghost" onClick={() => navigate('/admin/team')}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Btn>
      </div>

      <form
        onSubmit={save}
        className="grid xl:grid-cols-[minmax(0,1fr)_300px] gap-3 sm:gap-4 items-start"
      >
        <Card className="!p-4 sm:!p-5 space-y-3 sm:space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name">
              <input
                className={inputClass}
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
              />
            </Field>
            <Field label="Role / title">
              <input
                className={inputClass}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Meta Ads Expert"
              />
            </Field>
          </div>
          <Field label="Bio">
            <textarea
              className={areaClass}
              style={{ minHeight: 120 }}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="Short bio shown on the site"
            />
          </Field>
          <Field label="LinkedIn URL">
            <div className="relative">
              <Linkedin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className={`${inputClass} !pl-9`}
                value={form.linkedin_url}
                onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                placeholder="https://linkedin.com/in/…"
              />
            </div>
          </Field>

          {err && (
            <p className="text-sm text-red-600 rounded-xl bg-red-50 border border-red-100 px-3 py-2">
              {err}
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-primary/10 xl:hidden">
            <Btn type="submit" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Add member' : 'Save changes'}
            </Btn>
            <Btn variant="ghost" onClick={() => navigate('/admin/team')}>
              Cancel
            </Btn>
          </div>
        </Card>

        <aside className="space-y-3 xl:sticky xl:top-20 order-first xl:order-none">
          <Card className="!p-0 overflow-hidden">
            <div
              className={`px-4 py-3 border-b border-primary/10 ${
                form.is_active
                  ? 'bg-gradient-to-r from-emerald-600 to-emerald-500'
                  : 'bg-gradient-to-r from-navy-900 to-navy-800'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white/70">
                    Visibility
                  </p>
                  <p className="text-sm font-bold text-white">
                    {form.is_active ? 'Active on site' : 'Hidden'}
                  </p>
                </div>
                <div className="inline-flex rounded-xl bg-black/20 p-0.5 backdrop-blur">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_active: false })}
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                      !form.is_active
                        ? 'bg-white text-navy-900 shadow'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Hidden
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_active: true })}
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                      form.is_active
                        ? 'bg-white text-emerald-700 shadow'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Active
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <ImageUpload
                label="Photo"
                aspectClass="aspect-[4/5]"
                previewFit="cover"
                hint={IMAGE_SIZE_HINTS.team}
                value={form.photo_path}
                onChange={({ storedPath, url }) =>
                  setForm({
                    ...form,
                    photo_path: url || null,
                    photo_stored_path: storedPath,
                  })
                }
              />

              {form.photo_path && (
                <div className="flex items-center gap-3 rounded-2xl border border-primary/10 bg-slate-50/80 p-2.5">
                  <img
                    src={apiAssetUrl(form.photo_path)}
                    alt=""
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-navy-900 truncate">{form.name || 'Preview'}</p>
                    <p className="text-xs text-slate-500 truncate">{form.title || 'Role'}</p>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Sort order
                </span>
                <input
                  className={inputClass}
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  inputMode="numeric"
                />
                <p className="text-[0.7rem] text-slate-400">Lower numbers appear first</p>
              </div>

              <div className="[&_button]:w-full">
                <Btn type="submit" disabled={saving}>
                  {saving ? 'Saving…' : isNew ? 'Add member' : 'Save changes'}
                </Btn>
              </div>
            </div>
          </Card>
        </aside>
      </form>
    </div>
  )
}
